import { FormGroup, FormControlLabel } from '@mui/material'
import { Formik, FieldArray, Form } from 'formik'
import * as Yup from 'yup'
import Button from '@/components/common/Button'
import CustomFieldTypesEnum, {
  DYNAMIC_CF_TYPES,
} from '@/consts/CustomFieldTypesEnum'
import Colors from '@/styles/Colors.module.scss'
import { CustomFieldVisibility } from '@/consts/CustomFieldVisibility'
import SlideHeading from '@/components/common/SlideHeading'
import {
  SlideContainer,
  SlideContent,
  SlideActionButtons,
} from '@/components/common/Slide'
import InputField from '@/components/common/fields/InputField'
import SelectField from '@/components/common/fields/SelectField'
import MultiselectField from '@/components/common/fields/MultiselectField'
import { H6 } from '@/components/common/Typography'
import CustomFieldOption from '@/components/pages/manage/custom-fields/CustomFieldOption'
import buttonStyles from '@/styles/Buttons.module.scss'
import fieldStyles from '@/styles/Fields.module.scss'
import SwitchInput from '@/components/common/forms/formik/SwitchInput'
import CustomFieldFormEmojiPicker from '@/components/common/CustomFieldFormEmojiPicker'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { setAlert } from '@/store/slices/alertSlice'
import {
  createCustomField,
  updateCustomField,
} from '@/api/nextme/custom-fields'
import { useEffect, useMemo } from 'react'
import { printAxiosErrors } from '@/utils/util-functions'
import useQueues from '@/hooks/api-hooks/useQueues'
import { useTypedSelector } from '@/store/index'
import {
  CustomField,
  CustomFieldSettings,
  ParsedCustomField,
} from '@/api/interface/custom-field'
import { CFType } from '@/api/interface/utils'
import { Queue } from '@/api/interface/queue'
import useSelectedLocation from '@/hooks/api-selected-hooks/useSelectedLocation'
import useAddCFToSCI from '@/hooks/useAddCFToSCI'
import useIsUsingCustomQueueSelector from '@/hooks/useIsUsingCustomQueueSelector'
import useDynamicCF from '@/hooks/useDynamicCF'
import { canBeDynamicField, parseCfSettings } from '@/utils/custom-field-utils'

interface Props {
  customField: CustomField
  handleFormSuccess: Function
  canEditBusinessCF: boolean
  orderIndex: number
}

const multipleOptionTypes = [
  'select',
  'multiselect',
  'radio',
  'checkbox',
  'queueselect',
]

const FIELD_TYPE_OPTIONS = Object.keys(CustomFieldTypesEnum).map((key) => ({
  value: key,
  label: CustomFieldTypesEnum[key],
}))

const SELF_SERVICE_TYPE_OPTIONS = Object.entries(CustomFieldVisibility).map(
  ([k, v]) => ({
    value: v.value,
    label: v.label,
  })
)

const buildNewOption = () => ({
  id: null,
  cfId: null,
  index: 0,
  icon: '',
  value: '',
  valueMeta: '',
})

function CustomFieldForm({
  customField,
  handleFormSuccess,
  canEditBusinessCF,
  orderIndex,
}: Props) {
  const dispatch = useDispatch()
  const { sciToUpdate, updateSci, mutateSelfCheckIns } = useAddCFToSCI()

  const { locationId, businessId } = useTypedSelector((state) => state.selected)
  const { isUsingCustomQueueSelector } = useIsUsingCustomQueueSelector(false)

  const { hasOnlyOneLocation, isUsingSharedSettings } = useTypedSelector(
    (state) => state.pages.manage
  )

  const filteredFieldTypeOptions = useMemo(() => {
    if (isUsingCustomQueueSelector) {
      return FIELD_TYPE_OPTIONS.filter((item) => item.value !== 'queueselect')
    }
    return FIELD_TYPE_OPTIONS
  }, [isUsingCustomQueueSelector])

  const { selectedLocation } = useSelectedLocation()

  const { queues } = useQueues()
  const { queuesWithDynamicFields } = useDynamicCF()

  const sharedQueues = useMemo(
    () => queues.filter((x) => x.queueableType === 'business'),
    [queues]
  )

  const locationQueues = useMemo(
    () => queues.filter((x) => x.queueableId === locationId),
    [queues, locationId]
  )

  const isQueuePossible = (q: Queue) => {
    if (
      !customField &&
      (isUsingSharedSettings || hasOnlyOneLocation) &&
      q.queueableType === 'business' &&
      canEditBusinessCF
    ) {
      return true
    }

    if (
      !customField &&
      !(isUsingSharedSettings || hasOnlyOneLocation) &&
      q.queueableId === selectedLocation?.id
    ) {
      return true
    }

    if (
      customField?.cfableId === businessId &&
      q.queueableId === businessId &&
      canEditBusinessCF
    ) {
      return true
    }

    if (customField?.cfableId === locationId && q.queueableId === locationId) {
      return true
    }

    return false
  }

  const queuesOptions = useMemo(
    () =>
      [...sharedQueues, ...locationQueues].map((x) => ({
        value: x.id,
        label: x.name,
        isEnabled: customField?.enabledAt?.includes(x.id),
        impossible: !isQueuePossible(x),
      })),
    [customField, sharedQueues, locationQueues]
  )

  // an array of all possible queue ids
  // used to populate the values when creating a new CF
  const viewQueues = useMemo(
    () => queuesOptions.filter((x) => !x.impossible).map((x) => x.value),
    [queuesOptions]
  )

  const isNewCF = !customField?.id

  // an array of all currently enabled queue ids
  // used to populate the values of queues that are being edited and
  // may already have some queues enabled
  const editingCFInitialQueues = useMemo(() => {
    return queuesOptions
      .filter((item) => item.isEnabled)
      .map((item) => item.value)
  }, [queuesOptions])

  //* only business owner can affect business-level cf, so other users would make location-level cf's only
  const isSharedCF =
    isUsingSharedSettings || (hasOnlyOneLocation && canEditBusinessCF)

  const buildNewCustomField = (): ParsedCustomField => ({
    id: null,
    name: '',
    cfableId: isSharedCF ? businessId : locationId,
    cfableType: isSharedCF ? 'business' : 'location',
    enabledAt: viewQueues,
    index: orderIndex,
    isRequired: false,
    type: FIELD_TYPE_OPTIONS[0].value as CFType,
    editableByGuest: true,
    guestTypes: 'all',
    settings: {
      isDynamic: false,
    } as CustomFieldSettings,
    icon: '',
    visibility: 'all',
    options: [
      {
        ...buildNewOption(),
        id: null,
        cfId: null,
        index: 0,
        icon: '',
        value: '',
        valueMeta: '',
      },
    ],
  })

  const parsedSettings = useMemo(() => {
    if (customField?.settings) {
      return parseCfSettings(customField.settings)
    }
    return {}
  }, [customField])

  const initialValues = isNewCF
    ? buildNewCustomField()
    : {
        ...customField,
        editableByGuest: Boolean(
          customField?.editableByGuest || customField?.type === 'queueselect'
        ),
        settings: parsedSettings,
        isRequired: !!customField.isRequired,
        enabledAt: editingCFInitialQueues,
      }

  const cleanValuesForSubmission = (values: Partial<ParsedCustomField>) => {
    const isDynamicFieldPossible = canBeDynamicField(
      queuesWithDynamicFields,
      values
    )
    const jsonSettings = JSON.stringify({
      ...values.settings,
      isDynamic: isDynamicFieldPossible ? values.settings?.isDynamic : false,
    })

    return {
      ...values,
      options: multipleOptionTypes.includes(values.type)
        ? values.options
        : null,
      enabledAt: values.enabledAt,
      settings: jsonSettings,
    }
  }

  const onSubmit = async (values: Partial<ParsedCustomField>) => {
    try {
      const submission = cleanValuesForSubmission(values)

      if (isNewCF) {
        const storedCF = await createCustomField(submission)
        await updateSci(storedCF, sciToUpdate)
        mutateSelfCheckIns()
      } else {
        //* the API should guard against making changes to id or type
        const {
          cfableId: _cfableId,
          cfableType: _cfableType,
          ...patchPayload
        } = submission

        const updatedCF = await updateCustomField(patchPayload)
        await updateSci(updatedCF, sciToUpdate)
        mutateSelfCheckIns()
      }

      handleFormSuccess()

      dispatch(
        setAlert({
          isOpen: true,
          message: `Your custom field has been ${
            isNewCF ? 'created' : 'saved'
          }.`,
          title: `Success`,
          type: 'success',
        })
      )
    } catch (err) {
      console.error(printAxiosErrors(err))

      dispatch(
        setAlert({
          isOpen: true,
          title: err.response?.data?.message,
          type: 'error',
          message: printAxiosErrors(err, true),
        })
      )
    }
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    settings: Yup.object({
      dateRangeLimit: Yup.object({
        ageLimit: Yup.number().positive(),
      }),
    }),
  })

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        values,
        isValid,
        isSubmitting,
        setFieldValue,
        handleChange,
      }) => {
        const isDynamicFieldPossible = useMemo(() => {
          return canBeDynamicField(queuesWithDynamicFields, values)
        }, [values])

        useEffect(() => {
          if (!isDynamicFieldPossible) {
            setFieldValue('settings.isDynamic', false)
          }
        }, [isDynamicFieldPossible])

        values.options?.sort((a, b) => a.index - b.index)

        const lastOptionIndex = values.options?.slice(-1)[0]?.index || 0

        return (
          <Form noValidate onSubmit={handleSubmit}>
            <SlideContainer>
              <SlideContent>
                <SlideHeading
                  src={'/icons/custom-field.svg'}
                  title={`${isNewCF ? 'Add' : 'Edit'} Custom Field`}
                />
                <InputField label="Name" name="name" />

                <div
                  className="d-flex align-items-start"
                  style={{ gap: '2rem' }}
                >
                  <div className={classNames('flex-grow-1')}>
                    <SelectField
                      fullWidth={true}
                      label="Type"
                      name="type"
                      options={
                        values?.type !== 'queueselect'
                          ? filteredFieldTypeOptions
                          : FIELD_TYPE_OPTIONS
                      }
                    />
                  </div>
                  {!multipleOptionTypes.includes(values?.type) && (
                    <div className={classNames(fieldStyles.cfEmojiField)}>
                      <CustomFieldFormEmojiPicker
                        name="icon"
                        icon={values.icon}
                        anchor="right"
                      />
                    </div>
                  )}
                </div>

                <SelectField
                  fullWidth={true}
                  label="Visibility"
                  name="visibility"
                  options={SELF_SERVICE_TYPE_OPTIONS}
                />

                {values?.type !== 'queueselect' && (
                  <SelectField
                    fullWidth={true}
                    label="Guest Types"
                    name="guestTypes"
                    options={[
                      {
                        label: 'All',
                        value: 'all',
                      },
                      {
                        label: 'New',
                        value: 'new',
                      },
                      {
                        label: 'Returning',
                        value: 'returning',
                      },
                    ]}
                  />
                )}
                <SwitchInput
                  checked={values.isRequired}
                  onChange={handleChange}
                  name="isRequired"
                  label="Required"
                />
                <SwitchInput
                  disabled={!isDynamicFieldPossible}
                  checked={values?.settings?.isDynamic}
                  onChange={handleChange}
                  name="settings.isDynamic"
                  label="Enable Quick Editing"
                />
                <SwitchInput
                  disabled={values?.type === 'queueselect'}
                  checked={
                    (values?.editableByGuest as boolean) ||
                    customField?.type === 'queueselect'
                  }
                  onChange={handleChange}
                  name="editableByGuest"
                  label="Allow Repeat Guests to Edit Past Info"
                />

                <br />
                {values?.type === 'date' && (
                  <>
                    <div className={'field-group field-group-row'}>
                      <div className={'input-container'}>
                        <InputField
                          type="number"
                          min="0"
                          label="Age Limit (Years)"
                          name="settings.dateRangeLimit.ageLimit"
                          notes="If this is a birth date, specify an age limit or leave blank for no limit."
                        />
                      </div>
                    </div>
                    <br />
                  </>
                )}
                {multipleOptionTypes.includes(values?.type) && (
                  <>
                    <H6>Options</H6>
                    <FieldArray name="options">
                      {({ remove, push }) => (
                        <ul style={{ margin: '1rem 0' }}>
                          {values.options.map((x, i) => (
                            <CustomFieldOption
                              name={`options.${i}.icon`}
                              handleChange={handleChange}
                              // TODO: make drag-and-drop reordering possible
                              index={i}
                              key={`dropdownOption-${i}`}
                              remove={remove}
                              icon={x.icon}
                              value={x.value}
                              type={values.type}
                              queuesOptions={queuesOptions}
                              valueMetaName={`options.${i}.valueMeta`}
                              valueMeta={x.valueMeta}
                            />
                          ))}
                          <Button
                            style={{
                              fontSize: '16px',
                              marginLeft: 'auto',
                              borderRadius: '99px',
                              backgroundColor: Colors.$green,
                              color: Colors.$white,
                            }}
                            onClick={() => {
                              push({
                                ...buildNewOption(),
                                index: lastOptionIndex + 1,
                              })
                            }}
                            type="button"
                          >
                            Add Option
                          </Button>
                        </ul>
                      )}
                    </FieldArray>
                  </>
                )}
                {values.type !== 'queueselect' ? (
                  <MultiselectField
                    label="Enabled Queues"
                    name="enabledAt"
                    options={queuesOptions}
                    defaultValue={isNewCF ? viewQueues : values.enabledAt}
                    disable={viewQueues.length < 1}
                  />
                ) : (
                  <input
                    type="hidden"
                    name="enabledAt"
                    defaultValue={viewQueues}
                  />
                )}

                <br />
              </SlideContent>
              <SlideActionButtons>
                <button
                  className={classNames(
                    'btn w-100',
                    buttonStyles.enabledGreenBtn
                  )}
                  type="submit"
                  disabled={!isValid || isSubmitting}
                >
                  {isNewCF ? 'Create' : 'Save'}
                </button>
              </SlideActionButtons>
            </SlideContainer>
          </Form>
        )
      }}
    </Formik>
  )
}

export default CustomFieldForm
